import type { PageViewPayload } from '@/domain/beacon';
import { getAnalyticsPageUrl } from '@/lib/analyticsPageUrl';
import { MAX_PAGE_VIEW_BODY_BYTES, serializedBytes, trimPageContext } from '@/lib/beacon/trimPageContext';
import generateId from '@/lib/generateId';
import { logger } from '@/lib/logger';
import { extractPageContext } from '@/lib/pageContext';

/** Takes the payload rather than a gateway, so this module never learns the transport. */
export type SendPageView = (payload: PageViewPayload) => void;

export interface PageViewTrackerDeps {
  appId: string;
  clientId: string;
  /**
   * Called per send, not held: display rules put a launcher on one page and not the next,
   * and the tracker outlives a navigation. Configured launcher ids, not launcher types.
   */
  getLauncherIds: () => string[];
  /**
   * Whether this page shows an entry point. A page whose display rules hide every one is
   * a page the merchant has said not to operate on.
   */
  pageShowsEntryPoint: () => boolean;
  send: SendPageView;
}

export interface PageViewTracker {
  /**
   * Reports the current page, unless it is the one already reported. Reads the DOM as it
   * stands, so the caller owes it a page that has finished rendering — which is what
   * `Gladly.navigate()` means. Called mid-render it describes the outgoing page under the
   * incoming URL, and prompts are cached per page, so every later visitor gets that pair.
   */
  sendIfChanged(): void;
}

export function createPageViewTracker({
  appId,
  clientId,
  getLauncherIds,
  pageShowsEntryPoint,
  send,
}: PageViewTrackerDeps): PageViewTracker {
  // The last send only, not a history set: a page revisited after navigating away is a
  // fresh view and sends again.
  let lastSentPageIdentity: string | null = null;

  function sendIfChanged(): void {
    const pageIdentity = getAnalyticsPageUrl();
    // Fail closed: never send a page view we couldn't identify.
    if (!pageIdentity || pageIdentity === lastSentPageIdentity) return;
    if (!pageShowsEntryPoint()) {
      // Cleared, not left standing: a page the rules hide is not a baseline, and holding
      // the previous identity here swallows the send when the shopper returns to it.
      lastSentPageIdentity = null;
      return;
    }

    const payload = buildPageViewPayload({
      appId,
      clientId,
      launcherIds: getLauncherIds(),
      pageIdentity,
    });
    const payloadBytes = serializedBytes(payload);
    if (payloadBytes > MAX_PAGE_VIEW_BODY_BYTES) {
      // Sent regardless: the edge rejects it, and that rejection is how often this happens
      // becomes measurable. Sizes rather than the URL — the URL is what overran the cap.
      logger.warn('[pageView] page view exceeds the body cap; the edge will reject it', {
        cap: MAX_PAGE_VIEW_BODY_BYTES,
        payloadBytes,
      });
    }

    // Ordering matters: a throwing send must leave the identity unrecorded. Returning is
    // the whole bar — `send` is fire-and-forget, and a rejected request is not a retry.
    send(payload);
    lastSentPageIdentity = pageIdentity;
  }

  return { sendIfChanged };
}

const EMPTY_PAGE_CONTEXT: PageViewPayload['pageContext'] = {
  canonicalUrl: null,
  jsonLd: [],
  meta: {},
  title: '',
};

function buildPageViewPayload({
  appId,
  clientId,
  launcherIds,
  pageIdentity,
}: {
  appId: string;
  clientId: string;
  launcherIds: string[];
  pageIdentity: string;
}): PageViewPayload {
  const payload: PageViewPayload = {
    appId,
    clientId,
    launcherIds,
    pageContext: EMPTY_PAGE_CONTEXT,
    pageIdentity,
    queryKeys: extractQueryKeys(window.location.search),
    requestId: generateId(),
  };

  // Cost the rest first, so a long URL takes it out of the context rather than the cap.
  const remainingBytes = MAX_PAGE_VIEW_BODY_BYTES - serializedBytes(payload);
  // Reading the host's DOM and parsing every JSON-LD block is the expensive part, so an
  // envelope that has already spent the cap skips it rather than discarding the result.
  if (remainingBytes <= 0) return payload;
  return { ...payload, pageContext: trimPageContext(extractPageContext(), remainingBytes) };
}

/**
 * `URLSearchParams` reports a segment carrying no `=` as a key, so a bare token arrives as
 * a "name" and puts back the PII `pageIdentity` strips. A name here needs a separator and
 * a value behind it. Still best-effort: a name that looks ordinary can carry PII, and only
 * an allowlist would catch that — which needs to know which names carry page-type signal.
 */
function extractQueryKeys(search: string): string[] {
  const names = new Set<string>();
  for (const segment of search.replace(/^\?/, '').split('&')) {
    const separator = segment.indexOf('=');
    if (separator <= 0) continue;
    // Base64 padding is an `=`, so a bare padded token splits at its own padding and
    // leaves nothing behind it. A real pair has a value; a token pretending to be one
    // does not.
    if (!segment.slice(separator + 1).replace(/=+$/, '')) continue;
    const name = decodeQueryKey(segment.slice(0, separator));
    if (name && !isAttributionKey(name)) names.add(name);
  }
  return [...names];
}

function decodeQueryKey(raw: string): string {
  try {
    return decodeURIComponent(raw.replace(/\+/g, ' '));
  } catch {
    // A malformed percent-escape is not a name we can report.
    return '';
  }
}

/**
 * They say where a shopper came from, never what page they are on, and they are the only
 * part of a URL that reliably differs between two shoppers on one page — so carrying them
 * makes the payload size, and anything derived from it, vary per visitor.
 */
const ATTRIBUTION_KEY_PREFIXES = ['utm_', 'hsa_', 'pk_', 'mtm_', 'piwik_'];
const ATTRIBUTION_KEYS = new Set<string>([
  '_ga',
  '_gl',
  'dclid',
  'epik',
  'fbclid',
  'gad_source',
  'gbraid',
  'gclid',
  'gclsrc',
  'igshid',
  'li_fat_id',
  'mc_cid',
  'mc_eid',
  'msclkid',
  'rdt_cid',
  's_kwcid',
  'sccid',
  'srsltid',
  'ttclid',
  'twclid',
  'wbraid',
  'yclid',
]);

function isAttributionKey(name: string): boolean {
  const lowered = name.toLowerCase();
  return ATTRIBUTION_KEYS.has(lowered) || ATTRIBUTION_KEY_PREFIXES.some((prefix) => lowered.startsWith(prefix));
}
